import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import SimpleDatePicker from "../../components/SimpleDatePicker";
import {
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  LineChart as ShowChartIcon,
  Activity as AutoGraphIcon,
} from "lucide-react";
import api from "../../services/api";
import { format, differenceInCalendarDays, subDays } from "date-fns";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const hexToRgba = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string") return `rgba(25,118,210,${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return `rgba(25,118,210,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
};

const KpiPill = ({ icon, label, value, accent, isDark }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.25}
    sx={{
      px: 1.75,
      py: 1.25,
      borderRadius: 2.5,
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
      minWidth: 0,
      flex: 1,
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${accent} 0%, ${hexToRgba(accent, 0.7)} 100%)`,
        color: "#fff",
        boxShadow: `0 4px 12px ${hexToRgba(accent, 0.3)}`,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "text.secondary", lineHeight: 1.2 }} noWrap>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: "text.primary", letterSpacing: -0.3, lineHeight: 1.2 }} noWrap>
        {value}
      </Typography>
    </Box>
  </Stack>
);

export const ChartsDate = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const PRIMARY_CONTRAST = themeV4?.palette?.primary?.contrastText || "#fff";

  const isDark = (themeV4?.palette?.type || themeV4?.mode) === "dark";
  const axisColor = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,23,42,0.55)";
  const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)";
  const emptyBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)";
  const tooltipBg = isDark ? "rgba(20,24,32,0.95)" : "rgba(255,255,255,0.98)";
  const tooltipBorder = isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)";

  const [initialDate, setInitialDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [finalDate, setFinalDate] = useState(new Date());
  const [ticketsData, setTicketsData] = useState({ data: [], count: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [preset, setPreset] = useState("month");
  const { user } = useContext(AuthContext);
  const companyId = user.companyId;

  const chartRef = useRef(null);

  useEffect(() => {
    if (companyId) handleGetTicketsInformation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleGetTicketsInformation = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get(
        `/dashboard/ticketsDay?initialDate=${format(initialDate, "yyyy-MM-dd")}&finalDate=${format(finalDate, "yyyy-MM-dd")}&companyId=${companyId}`
      );
      setTicketsData(data);
    } catch {
      toast.error(i18n.t("dashboard.charts.errorFetchingTickets"));
    } finally {
      setIsLoading(false);
    }
  };

  const applyPreset = (value) => {
    if (!value) return;
    const now = new Date();
    let start = initialDate;
    if (value === "today") start = now;
    else if (value === "7d") start = subDays(now, 6);
    else if (value === "15d") start = subDays(now, 14);
    else if (value === "30d") start = subDays(now, 29);
    else if (value === "month") start = new Date(now.getFullYear(), now.getMonth(), 1);
    setPreset(value);
    setInitialDate(start);
    setFinalDate(now);
  };

  const stats = useMemo(() => {
    const arr = Array.isArray(ticketsData?.data) ? ticketsData.data : [];
    const total = ticketsData?.count || arr.reduce((acc, x) => acc + (Number(x.total) || 0), 0);
    const days = Math.max(1, differenceInCalendarDays(finalDate, initialDate) + 1);
    const avgPerDay = total / days;
    const peak = arr.reduce((mx, x) => Math.max(mx, Number(x.total) || 0), 0);
    return { total, avgPerDay, peak, days };
  }, [ticketsData, initialDate, finalDate]);

  const lineData = useMemo(() => {
    const labels =
      ticketsData?.data?.length > 0
        ? ticketsData.data.map((item) => (Object.prototype.hasOwnProperty.call(item, "horario") ? `${item.horario}:00` : item.data))
        : [];
    const values = ticketsData?.data?.length > 0 ? ticketsData.data.map((item) => Number(item.total) || 0) : [];
    return { labels, values };
  }, [ticketsData]);

  const dataCharts = {
    labels: lineData.labels,
    datasets: [
      {
        label: i18n.t("dashboard.charts.ticketsLabel"),
        data: lineData.values,
        borderColor: PRIMARY_MAIN,
        borderWidth: 2.5,
        tension: 0.4,
        fill: true,
        backgroundColor: (ctx) => {
          const { chart } = ctx;
          const { ctx: c, chartArea } = chart;
          if (!chartArea) return hexToRgba(PRIMARY_MAIN, 0.15);
          const grad = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          grad.addColorStop(0, hexToRgba(PRIMARY_MAIN, isDark ? 0.45 : 0.35));
          grad.addColorStop(1, hexToRgba(PRIMARY_MAIN, 0));
          return grad;
        },
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: PRIMARY_MAIN,
        pointHoverBorderWidth: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: isDark ? "#e9edef" : "#0f172a",
        bodyColor: isDark ? "#cfd6dc" : "#475569",
        borderColor: tooltipBorder,
        borderWidth: 1,
        boxPadding: 8,
        padding: 12,
        cornerRadius: 10,
        usePointStyle: true,
        displayColors: true,
        titleFont: { weight: 700, size: 12 },
        bodyFont: { size: 13, weight: 600 },
        callbacks: {
          labelColor: () => ({
            borderColor: PRIMARY_MAIN,
            backgroundColor: PRIMARY_MAIN,
            borderWidth: 0,
            borderRadius: 4,
          }),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: { color: axisColor, font: { size: 11, weight: 500 }, maxRotation: 0, autoSkipPadding: 16 },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: gridColor, drawBorder: false, drawTicks: false },
        ticks: { color: axisColor, font: { size: 11, weight: 500 }, padding: 8, precision: 0 },
        border: { display: false },
      },
    },
    elements: { line: { capBezierPoints: true } },
  };

  const presetOptions = [
    { value: "today", label: i18n.t("dashboard.charts.presets.today") || "Hoje" },
    { value: "7d", label: "7d" },
    { value: "15d", label: "15d" },
    { value: "30d", label: "30d" },
    { value: "month", label: i18n.t("dashboard.charts.presets.month") || "Mês" },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "text.primary", letterSpacing: -0.2 }}>
          {i18n.t("dashboard.users.totalAttendances")}
        </Typography>
        <Typography sx={{ mt: 0.25, fontSize: 12, color: "text.secondary" }}>
          {format(initialDate, "dd/MM/yyyy")} — {format(finalDate, "dd/MM/yyyy")} · {stats.days} {i18n.t(stats.days === 1 ? "dashboard.charts.day" : "dashboard.charts.days")}
        </Typography>
      </Box>

      <Grid container spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={5} md={4}>
          <SimpleDatePicker
            value={initialDate}
            onChange={(newValue) => {
              setInitialDate(newValue);
              setPreset(null);
            }}
            label={i18n.t("dashboard.date.initialDate")}
          />
        </Grid>
        <Grid item xs={12} sm={5} md={4}>
          <SimpleDatePicker
            value={finalDate}
            onChange={(newValue) => {
              setFinalDate(newValue);
              setPreset(null);
            }}
            label={i18n.t("dashboard.date.finalDate")}
          />
        </Grid>
        <Grid item xs={12} sm={2} md>
          <Button
            onClick={handleGetTicketsInformation}
            variant="contained"
            fullWidth
            disabled={isLoading}
            sx={{
              background: `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${PRIMARY_DARK} 100%)`,
              color: PRIMARY_CONTRAST,
              transition: "all .2s ease-in-out",
              borderRadius: "12px",
              py: 1,
              fontWeight: 700,
              boxShadow: `0 6px 16px ${hexToRgba(PRIMARY_MAIN, 0.35)}`,
              "&:hover": {
                background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_MAIN} 100%)`,
                transform: "translateY(-1px)",
                boxShadow: `0 10px 22px ${hexToRgba(PRIMARY_MAIN, 0.45)}`,
              },
            }}
          >
            {isLoading ? <CircularProgress size={22} color="inherit" /> : i18n.t("dashboard.buttons.filter")}
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2.5 }}>
        <ToggleButtonGroup
          value={preset}
          exclusive
          size="small"
          onChange={(e, v) => {
            applyPreset(v);
            setTimeout(() => handleGetTicketsInformation(), 0);
          }}
          sx={{
            background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
            border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
            borderRadius: 999,
            p: 0.5,
            "& .MuiToggleButton-root": {
              border: 0,
              borderRadius: "999px !important",
              px: 1.5,
              py: 0.5,
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
              color: "text.secondary",
              "&.Mui-selected": {
                background: isDark ? "#1c2230" : "#ffffff",
                color: "text.primary",
                boxShadow: isDark
                  ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 4px 12px rgba(0,0,0,0.4)"
                  : "0 1px 0 rgba(255,255,255,0.9) inset, 0 4px 12px rgba(15,23,42,0.06)",
                "&:hover": { background: isDark ? "#22293a" : "#ffffff" },
              },
              "&:hover": { background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)" },
            },
          }}
        >
          {presetOptions.map((p) => (
            <ToggleButton key={p.value} value={p.value}>
              {p.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}>
          <KpiPill
            icon={<TrendingUpIcon size={18} />}
            label={i18n.t("dashboard.charts.ticketsLabel") || "Total"}
            value={stats.total.toLocaleString()}
            accent={PRIMARY_MAIN}
            isDark={isDark}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiPill
            icon={<AutoGraphIcon size={18} />}
            label={i18n.t("dashboard.charts.avgPerDay") || "Média/dia"}
            value={stats.avgPerDay.toFixed(1)}
            accent="#2EA85A"
            isDark={isDark}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <KpiPill
            icon={<ShowChartIcon size={18} />}
            label={i18n.t("dashboard.charts.peak") || "Pico"}
            value={stats.peak.toLocaleString()}
            accent="#F7C82C"
            isDark={isDark}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 3,
          background: isDark
            ? `linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 100%)`
            : `linear-gradient(180deg, ${hexToRgba(PRIMARY_MAIN, 0.025)} 0%, transparent 100%)`,
          border: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(15,23,42,0.05)",
          mb: 2,
        }}
      >
        <Box sx={{ height: { xs: 260, sm: 320 }, position: "relative" }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <CircularProgress sx={{ color: PRIMARY_MAIN }} />
            </Box>
          ) : ticketsData?.data.length === 0 ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1.5}
              sx={{ height: "100%", bgcolor: emptyBg, borderRadius: 2 }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: hexToRgba(PRIMARY_MAIN, isDark ? 0.15 : 0.10),
                  color: PRIMARY_MAIN,
                }}
              >
                <ShowChartIcon size={28} />
              </Box>
              <Typography sx={{ fontSize: 13, color: "text.secondary", textAlign: "center", maxWidth: 320 }}>
                {i18n.t("dashboard.charts.noDataAvailable")}
              </Typography>
            </Stack>
          ) : (
            <Line ref={chartRef} options={options} data={dataCharts} />
          )}
        </Box>
      </Box>

    </Box>
  );
};

export default ChartsDate;
